<template>
  <Modal
    v-model:visible="isVisible"
    class="rate-select-and-tender-modal"
    :header="
      isBrokerageQuote
        ? t('shipments.tenderToBroker')
        : t('shipments.tenderToCarrier')
    "
    no-scroll
    @hide="onClose"
  >
    <div class="flex flex-column flex-gap-1">
      <Message
        v-if="tenderAuthorization.error"
        severity="error"
        class="rygen-message"
      >
        <div class="flex flex-gap-1 justify-content-between align-items-center">
          <span>{{ tenderAuthorization.error }}</span>

          <RygenButton
            mode="tertiary"
            icon="pi pi-replay"
            :label="t('common.retry')"
            :disabled="tenderAuthorization.loading"
            :severity="Severity.DANGER"
            @click="authorizeTender(rateQuote.id)"
          />
        </div>
      </Message>
      <Message
        v-else-if="wouldExceedCreditLimit && !tenderAuthorization.loading"
        severity="warn"
        class="rygen-message"
      >
        {{ t('creditLimits.willBeExceeded') }}
      </Message>
      <p v-if="isTenderOnly" class="select-tender-or-assign-message">
        {{ t('rateShop.assignAndTenderModal.selectTender') }}
      </p>
      <p v-else-if="canTenderToCarrier" class="select-tender-or-assign-message">
        {{ t('rateShop.assignAndTenderModal.selectTenderOrAssign') }}
      </p>

      <TenderActionGroupDropdown
        id="tender-action-group-dropdown"
        v-model="rateSelectAndTenderData.selectedDropdownValue"
        :tender-config="internalCarrierSetting.data?.tenderConfig"
        :default-tender-method="defaultTenderMethod"
        :rate-strategy-type="rateQuote.rateStrategyType"
        :is-tender-only="isTenderOnly"
        :loading="internalCarrierSetting.loading"
        data-testid="tender-modal-tender-action-dropdown"
        @update:model-value="onTenderOptionChange"
      />

      <TenderConfigOptionsSection
        v-if="!isAssignAction"
        id="tender-config-options-section"
        v-model="rateSelectAndTenderData.configOptions"
        :rate-quote="rateQuote"
        :selected-tender-method="selectedTenderMethod"
        :is-tender-only="isTenderOnly"
      />
    </div>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        :disabled="rateSelectAndTenderData.isAwaitingTenderSent || isLoading"
        @click="isVisible = false"
      />
      <RygenButton
        v-if="isAssignAction"
        mode="primary"
        :label="t('shipments.assign')"
        :loading="isLoading"
        @click="onAssignToShipment"
      />
      <RygenButton
        v-else
        mode="primary"
        :label="
          isConfirmTenderAction ? t('common.confirm') : t('shipments.tender')
        "
        :loading="rateSelectAndTenderData.isAwaitingTenderSent || isLoading"
        @click="onTenderToCarrier"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useUid, useValidations } from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValue } from '@rygen/rygen-ui/i18n';
import {
  CapacityProviderType,
  CarrierSetting,
  DropdownOption,
  Loadable,
  Mode,
  Optional,
  Severity,
  TenderConfig,
  TenderConfigOptions,
  TenderMethod,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { RateQuote, TenderRequest } from '@/model';
import { useCreditUsageStore } from '@/stores/CreditUsage';

import TenderActionGroupDropdown from '@/components/rateShop/assignAndTender/TenderActionGroupDropdown.vue';
import TenderConfigOptionsSection from '@/components/rateShop/assignAndTender/TenderConfigOptionsSection.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Message from 'primevue/message';

export interface GroupDropdownOption<T> {
  label: string;
  code: string;
  items: DropdownOption<T>[];
}

type RateSelectAndTender = {
  selectedDropdownValue: DropdownOption<string>;
  configOptions: TenderConfigOptions;
  isAwaitingTenderSent: boolean;
};

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  rateQuote: {
    type: RateQuote,
    required: true,
  },
  carrierSetting: {
    type: CarrierSetting,
    default: () => new CarrierSetting(),
  },
  tenderRequest: {
    type: TenderRequest,
    default: undefined,
  },
  isTenderOnly: {
    type: Boolean,
    default: false,
  },
});
const { rateQuote, carrierSetting, tenderRequest, isTenderOnly } =
  toRefs(props);

const emit = defineEmits<{
  (
    event: 'assign-and-tender',
    rateQuote: RateQuote,
    tenderMethod: TenderMethod,
    tenderConfigOptions: TenderConfigOptions,
    confirmTenderSent: () => void,
  ): void;
  (event: 'assign-rate-only', rateQuote: RateQuote): void;
  (
    event: 'tender-to-carrier-only',
    rateQuote: RateQuote,
    tenderMethod: TenderMethod,
    tenderConfigOptions: TenderConfigOptions,
    confirmTenderSent: () => void,
  ): void;
}>();

const { t } = useI18n();
const { uid } = useUid('rate-assign-and-tender-modal');
const {
  loadCreditUsage,
  performTenderAuthorization,
  canTenderToCarrier,
  wouldExceedCreditLimit,
  tenderAuthorization,
} = useCreditUsageStore();

const rateSelectAndTenderData: Ref<RateSelectAndTender> = ref({
  selectedDropdownValue: mapEnumToTranslatedDropdownValue(
    TenderMethod.MANUAL,
    'TenderMethod',
  ),
  configOptions: new TenderConfigOptions(),
  isAwaitingTenderSent: false,
});
const { v$, validate } = useValidations(rateSelectAndTenderData, {}, uid);

const internalCarrierSetting: Ref<Loadable<CarrierSetting>> = ref({
  loading: false,
});

const defaultTenderConfig = computed((): Optional<TenderConfig> => {
  return internalCarrierSetting.value.data?.tenderConfig?.find(
    (config: TenderConfig) => config.defaultMethod,
  );
});

const defaultTenderMethod = computed(() => defaultTenderConfig.value?.method);

const tenderMethodAndAssignFlags = computed(() => {
  const selectedMethod =
    rateSelectAndTenderData.value.selectedDropdownValue.value;
  const isParcelRate = rateQuote.value.mode === Mode.PARCEL;

  return {
    isApi: selectedMethod === TenderMethod.API && !isParcelRate,
    isManual: selectedMethod === TenderMethod.MANUAL,
    isP44:
      selectedMethod === TenderMethod.P44 ||
      selectedMethod === TenderMethod.P44_VOLUME_LTL,
    isAssign: selectedMethod === 'ASSIGN',
  };
});

const isConfirmTenderAction = computed(
  () =>
    tenderMethodAndAssignFlags.value.isApi ||
    tenderMethodAndAssignFlags.value.isManual ||
    tenderMethodAndAssignFlags.value.isP44,
);

const isAssignAction = computed(
  () => tenderMethodAndAssignFlags.value.isAssign,
);

const isBrokerageQuote = computed(() => rateQuote.value.source === 'BROKERAGE');

const selectedTenderMethod = computed(() => {
  return TenderMethod[
    rateSelectAndTenderData.value.selectedDropdownValue
      .value as keyof typeof TenderMethod
  ];
});

const isLoading = computed(() => {
  return (
    tenderAuthorization.value.loading || internalCarrierSetting.value.loading
  );
});

const onTenderOptionChange = () => {
  // Use computed prop instead of payload from event since 'ASSIGN' is not
  // part of the enum
  const tenderConfigOptions = findTenderConfigOptionsForSelectedMethod(
    selectedTenderMethod.value,
  );

  if (tenderConfigOptions) {
    rateSelectAndTenderData.value.configOptions = new TenderConfigOptions(
      tenderConfigOptions,
    );
  }
};

const findTenderConfigOptionsForSelectedMethod = (
  selectedMethod: Optional<TenderMethod>,
): Optional<TenderConfigOptions> => {
  if (!selectedMethod) {
    return undefined;
  }

  if (
    tenderRequest?.value?.tenderConfigOptions &&
    tenderRequest.value.tenderMethod === selectedMethod
  ) {
    return tenderRequest.value.tenderConfigOptions;
  }

  if (internalCarrierSetting.value.data) {
    const matchingTenderConfig =
      internalCarrierSetting.value.data.tenderConfig.find(
        (config) => config.method === selectedMethod,
      );

    return matchingTenderConfig?.configOptions;
  }
};

const onClose = () => {
  rateSelectAndTenderData.value = {
    selectedDropdownValue: mapEnumToTranslatedDropdownValue(
      TenderMethod.MANUAL,
      'TenderMethod',
    ),
    configOptions: new TenderConfigOptions(),
    isAwaitingTenderSent: false,
  };

  internalCarrierSetting.value.data = undefined;
  tenderAuthorization.value.data = undefined;
};

const onAssignToShipment = () => {
  emit('assign-rate-only', rateQuote.value);
  isVisible.value = false;
};

const onTenderToCarrier = async () => {
  if (!(await validate())) {
    return;
  }

  const tenderMethod =
    TenderMethod[
      rateSelectAndTenderData.value.selectedDropdownValue
        .value as keyof typeof TenderMethod
    ];

  rateSelectAndTenderData.value.isAwaitingTenderSent = true;
  if (isTenderOnly.value) {
    tenderToCarrier(tenderMethod);
  } else {
    assignToShipmentAndTender(tenderMethod);
  }

  isVisible.value = false;
};

const tenderToCarrier = (tenderMethod: TenderMethod) => {
  emit(
    'tender-to-carrier-only',
    rateQuote.value,
    tenderMethod,
    rateSelectAndTenderData.value.configOptions,
    () => (rateSelectAndTenderData.value.isAwaitingTenderSent = false),
  );
};

const assignToShipmentAndTender = (tenderMethod: TenderMethod) => {
  emit(
    'assign-and-tender',
    rateQuote.value,
    tenderMethod,
    rateSelectAndTenderData.value.configOptions,
    () => (rateSelectAndTenderData.value.isAwaitingTenderSent = false),
  );
};

const initializeModal = async () => {
  await loadCreditUsage();
  await authorizeTender(rateQuote.value.id);
  if (carrierSetting.value.carrier) {
    // RateQuote has been assigned to Shipment
    internalCarrierSetting.value.data = new CarrierSetting(
      carrierSetting.value,
    );
  } else {
    // RateShop phase, rate has not been assigned so there is no "active" carrierSettings
    internalCarrierSetting.value.data = await getCarrierSetting();
  }
  if (!canTenderToCarrier.value) {
    rateSelectAndTenderData.value.selectedDropdownValue = {
      label: t('rateShop.assignAndTenderModal.assignWithoutTendering'),
      value: 'ASSIGN',
    };
  } else {
    initializeRateSelectAndTenderData();
  }
};

const initializeRateSelectAndTenderData = () => {
  // TenderRequest has the last "active" tender configs while Shipment status
  // is at least Tendered or Tender Rejected
  if (tenderRequest?.value) {
    rateSelectAndTenderData.value.configOptions = new TenderConfigOptions(
      tenderRequest.value.tenderConfigOptions,
    );
    rateSelectAndTenderData.value.selectedDropdownValue =
      mapEnumToTranslatedDropdownValue(
        tenderRequest.value.tenderMethod,
        'TenderMethod',
      );
  } else if (defaultTenderConfig.value) {
    rateSelectAndTenderData.value.configOptions = new TenderConfigOptions(
      defaultTenderConfig.value.configOptions,
    );

    rateSelectAndTenderData.value.selectedDropdownValue =
      mapEnumToTranslatedDropdownValue(
        defaultTenderConfig.value.method,
        'TenderMethod',
      );
  }
};

const authorizeTender = async (rateQuoteId?: number) => {
  if (rateQuoteId) {
    await performTenderAuthorization(rateQuoteId);
  }
};

const getCarrierSetting = async (): Promise<CarrierSetting | undefined> => {
  const scac = rateQuote.value.carrier?.scac;
  const carrierType =
    rateQuote.value.carrier?.type ?? CapacityProviderType.CARRIER;

  if (!scac || !authorization.hasAuthority('client', 'read')) {
    return undefined;
  }

  try {
    internalCarrierSetting.value.loading = true;
    return carrierType === CapacityProviderType.CARRIER
      ? await api.carrier.findCarrierSettingsForScac(scac)
      : await api.broker.findBrokerSettingsForScac(scac);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.warn(
      t('errors.loadingParam', {
        param: 'carrier settings: Carrier setting my not be configured yet',
      }),
      e,
    );
  } finally {
    internalCarrierSetting.value.loading = false;
  }
};

watch(
  isVisible,
  (newVal: boolean) => {
    if (newVal) {
      initializeModal();
      v$.value.$reset();
    }
  },
  { immediate: true },
);
</script>

<style>
.rate-select-and-tender-modal {
  .tender-config-options-section {
    border-top: var(--divider);
    padding: var(--sp-3) 0 0 0;
  }
}
</style>
